import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import useMediaQuery from '@mui/material/useMediaQuery';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import Drawer from './Drawer';
import Header from './Header';
import Footer from './Footer';
import Loader from 'components/Loader';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import CelebrationOverlay from '../../sections/gamification/CelebrationOverlay';
import WelcomeTour from '../../sections/gamification/WelcomeTour';
import DateOfBirthPrompt from '../../sections/profile/DateOfBirthPrompt';
import { GuidesProvider } from '../../guides/GuidesContext';
import { GuideViewerHost } from '../../components/guides/GuideViewer';
import RetainerFlowGate from '../../sections/retainer/RetainerFlowGate';

// ==============================|| MAIN LAYOUT ||============================== //

export default function DashboardLayout() {
  const { menuMasterLoading } = useGetMenuMaster();
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  // Retainer ABN gate (RETAINER_ABN_SUPPLIER_PLAN.md 5.8): while it blocks the portal
  // (loading, the locked flow, the success screen), the other overlays stay unmounted.
  const [retainerGateActive, setRetainerGateActive] = useState(false);

  // set media wise responsive drawer
  useEffect(() => {
    handlerDrawerOpen(!downLG);
  }, [downLG]);

  if (menuMasterLoading) return <Loader />;

  return (
    // GuidesProvider: ONE shared guide registry read for every help surface
    // (Quick Action ?, mission rows, floating route help, Playbook) — avoids
    // a Firestore config read per affordance.
    <GuidesProvider>
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Header />
        <Drawer />
        {/* Retainer ABN flow: full-screen lock once a host has started it, on every page
            (self-gating; never while viewing as someone else). */}
        <RetainerFlowGate onActiveChange={setRetainerGateActive} />
        {!retainerGateActive && (
          <>
            {/* Gamification: full-screen celebration takeover on portal open
                (self-gating — no-op unless the brand runs missions and the viewer
                has unseen completions) */}
            <CelebrationOverlay />
            {/* Gamification: one-time welcome tour on first open after a brand
                flips missions on (self-gating; mounted after CelebrationOverlay so
                a first-run tour stacks ON TOP of any queued celebration) */}
            <WelcomeTour />
            {/* One-time ask to re-enter an unreadable date of birth (self-gating: only the
                signed-in host's own bad value, never while viewing as someone else) */}
            <DateOfBirthPrompt />
            {/* Host Playbook: the one guide viewer + floating per-route help
                button (self-gating — nothing renders on routes without a guide) */}
            <GuideViewerHost />
          </>
        )}

        <Box component="main" sx={{ width: 'calc(100% - 260px)', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
          <Toolbar sx={{ mt: 'inherit' }} />
          <Box
            sx={{
              ...{ px: { xs: 0, sm: 2 } },
              position: 'relative',
              minHeight: 'calc(100vh - 110px)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Breadcrumbs />
            <Outlet />
            <Footer />
          </Box>
        </Box>
      </Box>
    </GuidesProvider>
  );
}
